import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Leaf } from "lucide-react";

/**
 * Shared layout for legal / informational pages (Privacy, Terms, Refund).
 * Sets document title for SEO + provides a consistent typographic frame.
 */
export default function LegalLayout({ title, lastUpdated, intro, children, testid }) {
  useEffect(() => {
    document.title = `${title} · Feel Greats`;
    return () => {
      document.title = "Feel Greats — Smart Health Advisor";
    };
  }, [title]);

  return (
    <main className="bg-leaf-pattern min-h-[calc(100vh-4rem)] py-12" data-testid={testid || "legal-page"}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-800 transition-colors mb-6"
          data-testid="legal-home-link"
        >
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-emerald-700 text-white">
            <Leaf className="w-4 h-4" />
          </span>
          <span className="text-sm font-display font-semibold">Feel Greats</span>
        </Link>

        <header className="mb-10">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">
            {lastUpdated ? `Last updated ${lastUpdated}` : "Legal"}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-emerald-950 font-light tracking-tight mt-3">
            {title}
          </h1>
          {intro ? (
            <p className="text-emerald-900/70 mt-4 text-lg leading-relaxed">{intro}</p>
          ) : null}
        </header>

        <article className="legal-prose space-y-8 text-emerald-900/85 leading-relaxed">
          {children}
        </article>

        <div className="mt-16 pt-8 border-t border-emerald-100 text-sm text-emerald-900/60">
          Need a hand? Email{" "}
          <a
            href="mailto:support@feelgreats.co.uk"
            className="text-emerald-700 font-semibold hover:underline"
          >
            support@feelgreats.co.uk
          </a>{" "}
          and we'll get back to you within 1 business day.
        </div>
      </div>
    </main>
  );
}

export function Section({ heading, children, testid }) {
  return (
    <section data-testid={testid}>
      <h2 className="font-display text-2xl text-emerald-950 mb-3 font-medium">
        {heading}
      </h2>
      <div className="space-y-3 text-[15px]">{children}</div>
    </section>
  );
}
